import { API_HOST } from '@/app.consts';
import { ApiClient, QueryParams } from '@/api/api-client';
import { DatosConsorcioPendientes } from '../models/DatosConsorcioPendientes';
import { Consorcio } from '../../../models/Consorcio';
import { AcuerdoComercial } from '../../../models/AcuerdoComercial';
import { Plantilla } from '../models/Plantilla';
import {
  JsonApiResponseRecursos,
  JsonApiResponseRecurso
} from '@/api/json-api.types';
import { ApiModel } from '@/api/api-model';
import { Administracion } from '@/models/Administracion';
import { AcuerdoComercialConsorcio } from '../models/AcuerdoComercialConsorcio';
import { AcuerdoComercialTrial } from '../models/AcuerdoComercialTrial';
import { METADATA_GETTER_MAPEO_ESTADOS, METADATA_MUTATION_MAPEO_ESTADOS } from '@/store/metadata/metadata.types';
import store from '@/store';

const BASE_API_ACUERDO_COMERCIAL = '/sales/propuestas-comerciales';
const CONSORCIOS_PENDIENTES = '/consorcios/pendientes';

export const APIAcuerdoComercial = {

  /**
   * Metodo GET para obtener todos los consorcios pendientes a asignarle/crearles un AC
   * 
   * @returns arreglo de consorcios pendientes
   */
  getConsorciosPendientes: (params?: QueryParams): Promise<DatosConsorcioPendientes> => {
    let url = API_HOST + BASE_API_ACUERDO_COMERCIAL + CONSORCIOS_PENDIENTES;
    return ApiClient.getApiRecursos(url, params)
      .then(resp => {
        // Creo el modelo general
        const data_consorcios_pendientes = new DatosConsorcioPendientes();
        data_consorcios_pendientes.meta = resp.meta;

        // Trabajo los submodelos que tiene
        resp.data.forEach((data_consorcio) => {
          let consorcio_pendiente = new Consorcio();
          consorcio_pendiente.setDataFromApi(data_consorcio);
          data_consorcios_pendientes.consorcios.push(consorcio_pendiente);
        });

        if (resp.links) {
          data_consorcios_pendientes.api_links = resp.links;
        }
        return data_consorcios_pendientes;
      });
  },

  /**
   * Metodo GET para obtener todos los acuerdos comerciales, aplicandoles filtro
   * 
   * @param url string
   * @param params?  filtros
   * @returns arreglo de acuerdos comerciales filtradas
   */
  getAcuerdosComerciales: (url: string, params?: QueryParams): Promise<AcuerdoComercial[]> => {
    return ApiClient.getApiRecursos(url, params)
      .then(resp => {
        const acuerdos_comerciales = [];

        let administracion = null;
        if(resp.included){
          let administracion_datos = new Administracion();
          administracion_datos.setDataFromApi(resp.included[0]);
          administracion = administracion_datos;
        }

        // Seteo el mapeo de estados de los acuerdos comerciales en el store de metadata (si es que no existe ya y existe en el metadata de la respuesta). Este mapeo se usa en otros lugares en el satelite y el mapeo de estados lo obtendriamos del store de metadata
        if (resp.meta && resp.meta.mapeo_estados && !store.getters[METADATA_GETTER_MAPEO_ESTADOS]) {
          store.commit(METADATA_MUTATION_MAPEO_ESTADOS, resp.meta.mapeo_estados);
        }

        // Trabajo los submodelos que tiene
        resp.data.forEach((data_acuerdo_comercial) => {
          let acuerdo_comercial = new AcuerdoComercial();
          acuerdo_comercial.setDataFromApi(data_acuerdo_comercial);

          // Incorporo el consorcio si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.consorcio){
            let consorcio = new Consorcio();
            let data_consorcio = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.consorcio;
            consorcio.setDataFromApi(data_consorcio.data);
            acuerdo_comercial.consorcio = consorcio;
          }

          // Incorporo el acuerdo comercial del consorcio si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.propuesta_consorcio){
            let acuerdo_comercial_consorcio = new AcuerdoComercialConsorcio();
            let data_acuerdo_comercial_consorcio = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.propuesta_consorcio;
            acuerdo_comercial_consorcio.setDataFromApi(data_acuerdo_comercial_consorcio.data);
            acuerdo_comercial.propuesta_consorcio = acuerdo_comercial_consorcio;
          }

          // Incorporo la administración al acuerdo comercial si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.administracion){
            let acuerdo_comercial_administracion = new Administracion();
            let data_acuerdo_comercial_administracion = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.administracion;
            acuerdo_comercial_administracion.setDataFromApi(data_acuerdo_comercial_administracion.data);
            acuerdo_comercial.administracion = acuerdo_comercial_administracion;
          }

          if(administracion){
            acuerdo_comercial.administracion = administracion;
          }

          acuerdos_comerciales.push(acuerdo_comercial);
        });

        return acuerdos_comerciales;
      });
  },

  /**
   * Metodo GET para obtener todas las plantillas
   * 
   * @param url 
   * @returns arreglo de plantillas
   */
  getPlantillas: (url: string): Promise<Plantilla[]> => {
    return ApiClient.getApiRecursos(url)
      .then(resp => {
        const data_plantillas = [];

        // Trabajo los submodelos que tiene
        resp.data.forEach((data_plantilla) => {
          let plantilla = new Plantilla();
          plantilla.setDataFromApi(data_plantilla);
          data_plantillas.push(plantilla);
        });

        return data_plantillas;
      });
  },

  /**
   * Metodo para ejecutar el GET de un acuerdo comercial especifico
   * 
   * @param url 
   * @returns AcuerdoComercial
   */
  getAcuerdoComercial: (url: string): Promise<AcuerdoComercial> => {
    return ApiClient.getApiRecurso(url)
      .then(resp => {
        let ac_completo = new AcuerdoComercial();
        ac_completo.setDataFromApi(resp.data);

        // Incorporo el consorcio si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
        if (resp.data.relationships && resp.data.relationships.consorcio){
          let consorcio = new Consorcio();
          let data_consorcio = <JsonApiResponseRecurso> resp.data.relationships.consorcio;
          consorcio.setDataFromApi(data_consorcio.data);
          ac_completo.consorcio = consorcio;
        }

        return ac_completo;
      });
  },

  /**
   * Metodo POST que crear un acuerdo comercial
   * response: un arreglo de acuerdos comerciales 
   *  - Un elemento en caso de ser un AC libre | por default de la administración | vinculada a un consorcio
   *  - Varios elementos en caso que sea vinculada a varios consorcios
   */
  crearAcuerdoComercial: (link, acuerdo): Promise<AcuerdoComercial[]> => {
    return ApiClient.postApiRecurso(link, acuerdo)
      .then(resp => {
        let response:unknown =resp;
        let respuesta_casteada = response as JsonApiResponseRecursos;

        let arreglo_de_acs = [];
        respuesta_casteada.data.forEach((data_acuerdo_comercial) => {
          let acuerdo_comercial = new AcuerdoComercial();
          acuerdo_comercial.setDataFromApi(data_acuerdo_comercial);

          // Incorporo el consorcio si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.consorcio){
            let consorcio = new Consorcio();
            let data_consorcio = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.consorcio;
            consorcio.setDataFromApi(data_consorcio.data);
            acuerdo_comercial.consorcio = consorcio;
          }

          // Incorporo la administración si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.administracion){
            let administracion = new Administracion();
            let data_administracion = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.administracion;
            administracion.setDataFromApi(data_administracion.data);
            acuerdo_comercial.administracion = administracion;
          }

          // Incorporo el acuerdo comercial del consorcio si es que tiene (ya que no se infiere recursivamente desde el setDataFromApi)
          if (data_acuerdo_comercial.relationships && data_acuerdo_comercial.relationships.propuesta_consorcio){
            let acuerdo_comercial_consorcio = new AcuerdoComercialConsorcio();
            let data_acuerdo_comercial_consorcio = <JsonApiResponseRecurso> data_acuerdo_comercial.relationships.propuesta_consorcio;
            acuerdo_comercial_consorcio.setDataFromApi(data_acuerdo_comercial_consorcio.data);
            acuerdo_comercial.propuesta_consorcio = acuerdo_comercial_consorcio;
          }

          arreglo_de_acs.push(acuerdo_comercial);
        });

        return arreglo_de_acs;
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Metodo DELETE para llevar acabo la eliminación de un acuerdo comercial
   * @param link 
   */
  eliminarAcuerdoComercial: (link): Promise<JsonApiResponseRecurso> => {
    return ApiClient.deleteApiRecurso(link, new ApiModel())
      .then(resp => {
        return resp;
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Metodo PATCH para llevar acabo la modificación de un acuerdo comercial
   * Se vuelven a setear las relaciones del acuerdo comercial, porque sino se pierden los atributos calculados (Ej: el consorcio se ve vacio)
   * 
   * @param link
   * @param acuerdo_comercial_modificado 
   * @param acuerdo_comercial_original
   */
  actualizarAcuerdoComercial: (link, acuerdo_comercial_modificado, acuerdo_comercial_original): Promise<AcuerdoComercial> => {
    return ApiClient.patchApiRecurso(link, acuerdo_comercial_modificado, acuerdo_comercial_original).then(resp => {
      let acuerdo_editado = new AcuerdoComercial();
      acuerdo_editado.setDataFromApi(resp.data);

      // Seteamos de nuevo la relación de la administración al acuerdo comercial
      if(resp.data.relationships && resp.data.relationships.administracion) {
        const acuerdo_comercial_administracion = new Administracion();
        const data_acuerdo_comercial_administracion = <JsonApiResponseRecurso> resp.data.relationships.administracion;
        acuerdo_comercial_administracion.setDataFromApi(data_acuerdo_comercial_administracion.data);
        acuerdo_editado.administracion = acuerdo_comercial_administracion;
      }

      // Seteamos de nuevo la relación del consorcio al acuerdo comercial
      if (resp.data.relationships && resp.data.relationships.consorcio){
        const consorcio = new Consorcio();
        const data_consorcio = <JsonApiResponseRecurso> resp.data.relationships.consorcio;
        consorcio.setDataFromApi(data_consorcio.data);
        acuerdo_editado.consorcio = consorcio;
      }

      // Seteamos de nuevo la relación del acuerdo comercial del consorcio
      if (resp.data.relationships && resp.data.relationships.propuesta_consorcio){
        const acuerdo_comercial_consorcio = new AcuerdoComercialConsorcio();
        const data_acuerdo_comercial_consorcio = <JsonApiResponseRecurso> resp.data.relationships.propuesta_consorcio;
        acuerdo_comercial_consorcio.setDataFromApi(data_acuerdo_comercial_consorcio.data);
        acuerdo_editado.propuesta_consorcio = acuerdo_comercial_consorcio;
      }

      return acuerdo_editado;
    });
  },

  /**
   * [acuerdo_libre - no esta en uso actualmente]
   * Metodo PATCH para asignar un acuerdo comercial libre a un consorcio pendiente
   * 
   * @param link 
   * @param acuerdo_comercial_libre_seleccionado 
   * @returns acuerdo comercial asignado
   */
  asignarAcuerdoLibreAConsorcioPendiente: (link, acuerdo_comercial_libre_seleccionado): Promise<AcuerdoComercial> => {
    return ApiClient.patchApiRecurso(link, acuerdo_comercial_libre_seleccionado).then(resp => {
      let acuerdo_asignado = new AcuerdoComercial();
      acuerdo_asignado.setDataFromApi(resp.data);
      return acuerdo_asignado;
    });
  },

  /**
   * Metodo para realizar un patch editando el estado de archivado de los consorcios seleccionados
   * 
   * @returns 204 éxito / 402 error de validación
   */
  archivarConsorciosPendientes: (url: string, consorcios_pendientes): Promise<JsonApiResponseRecurso> => {
    return ApiClient.patchApiRecursos(url, consorcios_pendientes)
      .then((resp) => {
        return resp;
      })
      .catch((error) => {
        return error;
      });
  },

  /**
   * Método para extender el tiempo de trial de un acuerdo comercial
   * @param url 
   * @param acuerdo_comercial_trial 
   * @returns 204 éxito
   */
  extenderTiempoTrial: (url: string, acuerdo_comercial_trial: AcuerdoComercialTrial): Promise<JsonApiResponseRecurso> => {
    return ApiClient.patchApiRecurso(url, acuerdo_comercial_trial).then(resp => {
      return resp;
    });
  },

  /**
   * Método para finalizar el período de trial de un acuerdo comercial
   * @param url 
   * @param acuerdo_comercial_trial 
   * @returns 204 éxito
   */
  finalizarTiempoTrial: (url: string, acuerdo_comercial_trial: AcuerdoComercialTrial): Promise<JsonApiResponseRecurso> => {
    return ApiClient.patchApiRecurso(url, acuerdo_comercial_trial).then(resp => {
      return resp;
    });
  }
};